import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import pb from '../../lib/pocketbase';
import Select from 'react-select';
import { FaTimes } from 'react-icons/fa';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEdit, faClock, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import styles from './ProjectForm.module.css';

function TimesheetForm({ entry, onSave, onCancel, userRole }) {
    const currentUserId = pb.authStore.model?.id;

    const [formData, setFormData] = useState({
        worker: (entry && entry.id) ? (entry.worker || '') : (currentUserId ? [currentUserId] : []),
        chantier: '',
        date: new Date().toISOString().split('T')[0], // Today by default
        hours: '',
        morning_start: '',
        morning_end: '',
        afternoon_start: '',
        afternoon_end: '',
        description: ''
    });

    const [chantiers, setChantiers] = useState([]);
    const [employees, setEmployees] = useState([]); // For admin selection

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formErrors, setFormErrors] = useState({});

    // Smart UI is derived directly from formData to ensure perfect bidirectional sync
    const smartStart = formData.morning_start || '';
    const smartEnd = formData.afternoon_end || formData.morning_end || '';
    const hasPause = !!formData.afternoon_start;

    useEffect(() => {
        loadDependencies();
        if (entry) {
            setFormData({
                worker: entry.id ? (entry.worker || '') : (entry.worker ? [entry.worker] : (currentUserId ? [currentUserId] : [])),
                chantier: entry.chantier,
                date: entry.date ? entry.date.split(' ')[0] : '',
                hours: entry.hours,
                morning_start: entry.morning_start || '',
                morning_end: entry.morning_end || '',
                afternoon_start: entry.afternoon_start || '',
                afternoon_end: entry.afternoon_end || '',
                description: entry.description || ''
            });
        }
    }, [entry]);

    // Auto-calculate total hours if any time block is filled
    useEffect(() => {
        if (formData.morning_start || formData.morning_end || formData.afternoon_start || formData.afternoon_end) {
            const h = (s, e) => {
                if (!s || !e || s === "--:--" || e === "" || s === "") return 0;
                const [hS, mS] = s.split(':').map(Number);
                const [hE, mE] = e.split(':').map(Number);
                return Math.max(0, (hE + mE / 60) - (hS + mS / 60));
            };
            const total = h(formData.morning_start, formData.morning_end) + h(formData.afternoon_start, formData.afternoon_end);
            if (total > 0) {
                setFormData(prev => {
                    const parsedTotal = parseFloat(total.toFixed(2));
                    if (parseFloat(prev.hours) === parsedTotal) return prev;
                    return { ...prev, hours: parsedTotal };
                });
            }
        }
    }, [formData.morning_start, formData.morning_end, formData.afternoon_start, formData.afternoon_end]);

    const copyYesterday = async () => {
        try {
            let workerId = '';
            if (Array.isArray(formData.worker)) {
                if (formData.worker.length === 0) {
                    toast.warning("Sélectionnez d'abord un employé");
                    return;
                }
                workerId = formData.worker[0];
            } else {
                workerId = formData.worker;
            }

            if (!workerId) {
                toast.warning("Sélectionnez d'abord un employé");
                return;
            }

            const records = await pb.collection('timesheets').getList(1, 1, {
                filter: `worker = "${workerId}"`,
                sort: '-date',
            });

            if (records.items.length > 0) {
                const prev = records.items[0];
                setFormData(prevData => ({
                    ...prevData,
                    chantier: prev.chantier,
                    morning_start: prev.morning_start || '',
                    morning_end: prev.morning_end || '',
                    afternoon_start: prev.afternoon_start || '',
                    afternoon_end: prev.afternoon_end || '',
                    description: prev.description || ''
                }));
                
                toast.success("Horaires de la dernière saisie récupérés");
            } else {
                toast.info("Aucune saisie précédente trouvée pour cet employé");
            }
        } catch (e) {
            toast.error("Erreur lors de la copie");
        }
    };

    const loadDependencies = async () => {
        try {
            // 1. Load active chantiers
            const chantierData = await pb.collection('chantiers').getFullList({
                sort: '-created',
                filter: 'status != "ANNULE" && status != "TERMINE"' 
            });
            setChantiers(chantierData);

            // 2. If Admin, load employees to allow assignment
            if (userRole === 'admin') {
                const usersData = await pb.collection('users').getFullList({
                    sort: 'name',
                    filter: 'role != "client"'
                });
                setEmployees(usersData);
            }
        } catch (err) {
            console.error("Erreur chargement dépendances", err);
            toast.error("Erreur de chargement des listes");
        }
    };

    const handleSmartChange = (type, value) => {
        setFormData(prev => {
            if (type === 'start') {
                return { ...prev, morning_start: value };
            }
            if (type === 'end') {
                return {
                    ...prev,
                    ...(hasPause ? { afternoon_end: value } : { morning_end: value })
                };
            }
            if (type === 'pause') {
                if (value) { // Turning pause ON
                    const end = prev.afternoon_end || prev.morning_end || '17:00';
                    return { ...prev, morning_end: '12:00', afternoon_start: '13:00', afternoon_end: end };
                } else { // Turning pause OFF
                    const end = prev.afternoon_end || prev.morning_end || '16:00';
                    return { ...prev, morning_end: end, afternoon_start: '', afternoon_end: '' };
                }
            }
            return prev;
        });
    };

    const formatTimeStr = (val) => {
        if (!val) return '';
        let str = String(val).toLowerCase().replace('h', ':').replace('.', ':').replace(',', ':');
        if (!str.includes(':')) {
            if (str.length === 4 && !isNaN(str)) {
                str = str.substring(0,2) + ':' + str.substring(2,4);
            } else if (str.length === 3 && !isNaN(str)) {
                str = '0' + str.substring(0,1) + ':' + str.substring(1,3);
            } else {
                str = str + ':00';
            }
        }
        
        let [h, m] = str.split(':');
        h = h.padStart(2, '0');
        m = (m || '00').padEnd(2, '0').substring(0, 2);
        
        if (isNaN(h) || isNaN(m)) return '';
        if (parseInt(h) > 23) h = '23';
        if (parseInt(m) > 59) m = '59';

        return `${h}:${m}`;
    };

    const handleTimeBlur = (e) => {
        const { name, value } = e.target;
        if (!value) return;
        const formatted = formatTimeStr(value);
        if (formatted !== value) {
            setFormData(prev => ({ ...prev, [name]: formatted }));
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        
        setFormData(prev => {
            const next = { ...prev, [name]: value };
            
            if (name === 'hours') {
                const valStr = value.toString().replace(',', '.');
                const val = parseFloat(valStr);
                
                if (!isNaN(val) && val > 0) {
                    if (val <= 4) {
                        const endH = 8 + Math.floor(val);
                        const endM = Math.round((val % 1) * 60);
                        next.morning_start = '08:00';
                        next.morning_end = `${String(endH).padStart(2, '0')}:${String(endM).padStart(2, '0')}`;
                        next.afternoon_start = '';
                        next.afternoon_end = '';
                    } else {
                        const remaining = val - 4;
                        const endH = 13 + Math.floor(remaining);
                        const endM = Math.round((remaining % 1) * 60);
                        next.morning_start = '08:00';
                        next.morning_end = '12:00';
                        next.afternoon_start = '13:00';
                        next.afternoon_end = `${String(endH).padStart(2, '0')}:${String(endM).padStart(2, '0')}`;
                    }
                }
            }
            return next;
        });

        if (formErrors[name]) {
            setFormErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.chantier) errors.chantier = "Veuillez sélectionner un chantier";
        if (!formData.date) errors.date = "La date est requise";
        if (!formData.hours || formData.hours <= 0) errors.hours = "Nombre d'heures invalide";
        if (userRole === 'admin') {
            const isEditing = entry && entry.id;
            if (isEditing && !formData.worker) errors.worker = "Veuillez sélectionner un employé";
            if (!isEditing && (!formData.worker || formData.worker.length === 0)) errors.worker = "Veuillez sélectionner au moins un employé";
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsSubmitting(true);
        try {
            let record;
            const payload = { ...formData };
            payload.hours = Number(payload.hours);

            // Auto-approve if created by admin
            if (userRole === 'admin' && (!entry || !entry.id)) {
                payload.status = 'approved';
            }

            if (entry && entry.id) {
                record = await pb.collection('timesheets').update(entry.id, payload);
                toast.success("Heures mises à jour");
                onSave(record);
            } else {
                // Mode création : on gère le tableau d'employés
                const workers = Array.isArray(formData.worker) ? formData.worker : [formData.worker];
                const promises = workers.map(wId => {
                    return pb.collection('timesheets').create({
                        ...payload,
                        worker: wId
                    });
                });

                const results = await Promise.all(promises);
                toast.success(`${results.length} fiche(s) d'heures créée(s)`);
                // On renvoie le premier pour fermer le modal
                onSave(results[0]);
            }
        } catch (err) {
            console.error("Erreur sauvegarde heures", err);
            toast.error("Erreur : " + (err.message || "Impossible de sauvegarder"));
        } finally {
            setIsSubmitting(false);
        }
    };
    const handleDelete = async () => {
      if (!entry?.id) return;
      if (window.confirm('Confirmer la suppression de cet enregistrement ?')) {
        try {
          await pb.collection('timesheets').delete(entry.id);
          toast.success('Enregistrement supprimé');
          // Notify parent to refresh data
          if (onSave) {
            onSave();
          } else {
            onCancel();
          }
        } catch (e) {
          console.error('Erreur suppression', e);
          toast.error('Erreur lors de la suppression');
        }
      }
    };
    const employeeOptions = employees.map(u => ({
        value: u.id,
        label: `${u.name || u.username} (${u.role})`
    }));

    const chantierOptions = chantiers.map(c => ({
        value: c.id,
        label: `${c.name} (${c.status})`
    }));

    const modalContent = (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(4px)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 15 }}>
            <div style={{ background: 'var(--color-surface)', width: '100%', maxWidth: 650, borderRadius: 24, boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)', overflow: 'hidden', maxHeight: '95vh', display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>

                    <div style={{ padding: '20px 30px', borderBottom: '1px solid var(--color-light-gray)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <div style={{ width: 40, height: 40, borderRadius: 12, background: 'var(--color-light-background)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#3b82f6' }}>
                                <FontAwesomeIcon icon={(entry && entry.id) ? faEdit : faClock} size="lg" />
                            </div>
                            <div>
                                <h3 style={{ margin: 0, fontWeight: 900, color: 'var(--color-text)' }}>{(entry && entry.id) ? 'Modifier les Heures' : 'Saisir des Heures'}</h3>
                                <p style={{ margin: 0, fontSize: 11, color: 'var(--color-text-light)', fontWeight: 600 }}>Gestion du temps de travail</p>
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                            <button type="button" onClick={onCancel} style={{ background: 'var(--color-light-background)', border: 'none', cursor: 'pointer', color: 'var(--color-text-light)', width: 36, height: 36, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <FaTimes />
                            </button>
                        </div>
                    </div>

                    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
                        <div style={{ padding: '25px 30px', display: 'flex', flexDirection: 'column', gap: 20, overflowY: 'auto', flex: 1 }}>

                            {/* Admin: Select Worker */}
                            {userRole === 'admin' ? (
                                <div className={styles.formGroup}>
                                    <label htmlFor="worker">Employé *</label>
                                    <Select
                                        isMulti={!(entry && entry.id)}
                                        id="worker"
                                        name="worker"
                                        options={employeeOptions}
                                        value={
                                            !(entry && entry.id)
                                                ? employeeOptions.filter(o => Array.isArray(formData.worker) && formData.worker.includes(o.value))
                                                : employeeOptions.find(o => o.value === formData.worker) || null
                                        }
                                        onChange={(selected) => {
                                            if (!(entry && entry.id)) {
                                                // Mode Multi
                                                setFormData(prev => ({ ...prev, worker: selected ? selected.map(s => s.value) : [] }));
                                            } else {
                                                // Mode Single
                                                setFormData(prev => ({ ...prev, worker: selected ? selected.value : '' }));
                                            }
                                            if (formErrors.worker) setFormErrors(prev => ({ ...prev, worker: null }));
                                        }}
                                        placeholder="-- Sélectionner un employé --"
                                        isClearable
                                        isSearchable
                                        className="react-select-container"
                                        classNamePrefix="react-select"
                                    />
                                    {formErrors.worker && <div className={styles.fieldError}>{formErrors.worker}</div>}
                                </div>
                            ) : (
                                // Employee: Hidden Worker Field (Self) - Display Name just for info
                                <div className={styles.formGroup} style={{ opacity: 0.7 }}>
                                    <label>Employé</label>
                                    <input type="text" value={pb.authStore.model?.name || pb.authStore.model?.username || 'Moi'} disabled style={{ backgroundColor: '#f1f5f9' }} />
                                </div>
                            )}

                            <div className={styles.formGroup}>
                                <label htmlFor="chantier">Chantier *</label>
                                <Select
                                    id="chantier"
                                    name="chantier"
                                    options={chantierOptions}
                                    value={chantierOptions.find(o => o.value === formData.chantier) || null}
                                    onChange={(selected) => {
                                        setFormData(prev => ({ ...prev, chantier: selected ? selected.value : '' }));
                                        if (formErrors.chantier) setFormErrors(prev => ({ ...prev, chantier: null }));
                                    }}
                                    placeholder="-- Sélectionner un chantier --"
                                    isClearable
                                    isSearchable
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                />
                                {formErrors.chantier && <div className={styles.fieldError}>{formErrors.chantier}</div>}
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                <div className={styles.formGroup}>
                                    <label htmlFor="date">Date *</label>
                                    <input
                                        type="date"
                                        id="date"
                                        name="date"
                                        value={formData.date}
                                        onChange={handleChange}
                                    />
                                    {formErrors.date && <div className={styles.fieldError}>{formErrors.date}</div>}
                                </div>

                                <div className={styles.formGroup}>
                                    <label htmlFor="hours">Nombre d'heures Total *</label>
                                    <input
                                        type="number"
                                        step="0.5"
                                        id="hours"
                                        name="hours"
                                        value={formData.hours}
                                        onChange={handleChange}
                                        placeholder="Ex: 4 ou 7.5"
                                        style={{ background: 'rgba(164,198,57,0.05)', fontWeight: 900 }}
                                    />
                                    {formErrors.hours && <div className={styles.fieldError}>{formErrors.hours}</div>}
                                </div>
                            </div>

                            {/* Mode Rapide - Smart Fields & Presets */}
                            <div style={{ background: 'var(--color-light-background)', padding: '15px 20px', borderRadius: '12px', display: 'flex', flexDirection: 'column', gap: '15px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                                    <label style={{ fontSize: '0.8rem', color: 'var(--color-text)', fontWeight: 800, margin: 0 }}>⚡ Remplissage rapide</label>
                                    
                                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                        <button type="button" onClick={copyYesterday} style={{ background: 'var(--color-surface)', border: '1px solid var(--color-primary)', color: 'var(--color-primary)', padding: '6px 10px', borderRadius: '8px', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer' }}>🪄 Copier la veille</button>
                                        <button type="button" onClick={() => setFormData(prev => ({...prev, morning_start: '08:00', morning_end: '12:00', afternoon_start: '13:00', afternoon_end: '17:00'}))} style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', color: 'var(--color-text)', padding: '6px 10px', borderRadius: '8px', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer' }}>Journée (8h)</button>
                                        <button type="button" onClick={() => setFormData(prev => ({...prev, morning_start: '08:00', morning_end: '12:00', afternoon_start: '', afternoon_end: ''}))} style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', color: 'var(--color-text)', padding: '6px 10px', borderRadius: '8px', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer' }}>Matin (4h)</button>
                                        <button type="button" onClick={() => setFormData(prev => ({...prev, morning_start: '', morning_end: '', afternoon_start: '13:00', afternoon_end: '17:00'}))} style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', color: 'var(--color-text)', padding: '6px 10px', borderRadius: '8px', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer' }}>A-m (4h)</button>
                                        <button type="button" onClick={() => setFormData(prev => ({...prev, morning_start: '08:00', morning_end: '16:00', afternoon_start: '', afternoon_end: ''}))} style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', color: 'var(--color-text)', padding: '6px 10px', borderRadius: '8px', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer' }}>Continue</button>
                                    </div>
                                </div>

                                <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'wrap' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', background: 'var(--color-surface)', padding: '8px 15px', borderRadius: '12px', border: '1px solid var(--color-border)' }}>
                                        <label style={{ fontSize: '0.8rem', fontWeight: 700, margin: 0, color: 'var(--color-text)' }}>Début</label>
                                        <input type="text" value={smartStart} onChange={(e) => handleSmartChange('start', e.target.value)} onBlur={(e) => handleSmartChange('start', formatTimeStr(e.target.value))} style={{ border: 'none', background: 'transparent', fontWeight: 600, outline: 'none', width: '60px', color: 'var(--color-text)' }} placeholder="08:00" />
                                    </div>
                                    <span style={{ fontWeight: 800, color: 'var(--color-text-light)' }}>→</span>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', background: 'var(--color-surface)', padding: '8px 15px', borderRadius: '12px', border: '1px solid var(--color-border)' }}>
                                        <label style={{ fontSize: '0.8rem', fontWeight: 700, margin: 0, color: 'var(--color-text)' }}>Fin</label>
                                        <input type="text" value={smartEnd} onChange={(e) => handleSmartChange('end', e.target.value)} onBlur={(e) => handleSmartChange('end', formatTimeStr(e.target.value))} style={{ border: 'none', background: 'transparent', fontWeight: 600, outline: 'none', width: '60px', color: 'var(--color-text)' }} placeholder="17:00" />
                                    </div>
                                </div>

                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '5px' }}>
                                    <input 
                                        type="checkbox" 
                                        id="pauseToggle" 
                                        checked={hasPause} 
                                        onChange={(e) => handleSmartChange('pause', e.target.checked)}
                                        style={{ width: '18px', height: '18px', accentColor: 'var(--color-primary)' }}
                                    />
                                    <label htmlFor="pauseToggle" style={{ fontSize: '0.85rem', fontWeight: 600, margin: 0, cursor: 'pointer' }}>
                                        Pause le midi (12:00 - 13:00)
                                    </label>
                                </div>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', background: 'var(--color-light-background)', padding: '15px', borderRadius: '12px' }}>
                                <div className={styles.formGroup} style={{ marginBottom: 0 }}>
                                    <label style={{ fontSize: '0.8rem', color: 'var(--color-text-light)', fontWeight: 800 }}>MATIN</label>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <input type="text" name="morning_start" value={formData.morning_start} onChange={handleChange} onBlur={handleTimeBlur} style={{ padding: '8px', width: '80px', textAlign: 'center', color: 'var(--color-text)' }} placeholder="00:00" />
                                        <span style={{ color: 'var(--color-text)' }}>à</span>
                                        <input type="text" name="morning_end" value={formData.morning_end} onChange={handleChange} onBlur={handleTimeBlur} style={{ padding: '8px', width: '80px', textAlign: 'center', color: 'var(--color-text)' }} placeholder="00:00" />
                                    </div>
                                </div>
                                <div className={styles.formGroup} style={{ marginBottom: 0 }}>
                                    <label style={{ fontSize: '0.8rem', color: 'var(--color-text-light)', fontWeight: 800 }}>APRÈS-MIDI</label>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <input type="text" name="afternoon_start" value={formData.afternoon_start} onChange={handleChange} onBlur={handleTimeBlur} style={{ padding: '8px', width: '80px', textAlign: 'center', color: 'var(--color-text)' }} placeholder="00:00" />
                                        <span style={{ color: 'var(--color-text)' }}>à</span>
                                        <input type="text" name="afternoon_end" value={formData.afternoon_end} onChange={handleChange} onBlur={handleTimeBlur} style={{ padding: '8px', width: '80px', textAlign: 'center', color: 'var(--color-text)' }} placeholder="00:00" />
                                    </div>
                                </div>
                            </div>

                            <div className={styles.formGroup}>
                                <label htmlFor="description">Description des tâches</label>
                                <textarea
                                    id="description"
                                    name="description"
                                    rows="3"
                                    value={formData.description}
                                    onChange={handleChange}
                                    placeholder="Ex: Pose de cloisons, Peinture..."
                                ></textarea>
                            </div>

                        </div>

                        <div style={{ padding: '20px 30px', background: 'var(--color-light-background)', borderTop: '1px solid var(--color-light-gray)', display: 'flex', justifyContent: 'flex-end', gap: 10, flexShrink: 0 }}>
                            <button type="button" onClick={onCancel} disabled={isSubmitting} style={{ background: 'transparent', border: '1px solid var(--color-light-gray)', padding: '10px 20px', borderRadius: 8, fontWeight: 600, cursor: 'pointer', color: 'var(--color-text)' }}>
                                Annuler
                            </button>
                            {entry && entry.id && (
                              <button type="button" onClick={handleDelete} disabled={isSubmitting} style={{ background: '#dc2626', color: 'white', border: 'none', padding: '10px 20px', borderRadius: 8, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <FontAwesomeIcon icon={faTrashAlt} /> Supprimer
                              </button>
                            )}
                            <button type="submit" disabled={isSubmitting} style={{ background: 'var(--color-primary)', color: 'white', border: 'none', padding: '10px 20px', borderRadius: 8, fontWeight: 600, cursor: 'pointer' }}>
                                {isSubmitting ? 'Enregistrement...' : ((entry && entry.id) ? 'Mettre à jour' : 'Enregistrer')}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );

    return createPortal(modalContent, document.body);
}

export default TimesheetForm;
